import { Content } from "@app/models/Content";
import { Address } from "@app/models/Address";
import { Confirmation } from "@app/models/Confirmation";
import { ConvenienceFeeResponse } from "@app/models/ConvenienceFeeResponse";
import { CouponData } from "@app/models/CouponData";
import { PaymentHistory } from "@app/models/PaymentHistory";
import { PaymentSchedule } from "@app/models/PaymentSchedule";
import { Property } from "@app/models/Property";
import { ValidationResponse } from "@app/models/ValidationResponse";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { HOAApiBase } from "@app/services/apiServices/utils/HOAApiBase";
import { HttpResponse } from "@angular/common/http";

export type PropertyAddressLineItemDetail = {
    // https://webitg4.mutualofomaha.com/hoa-gateway/swagger-ui.html#/Property/propertyBillingDetailsLookupByCouponIdsGetUsingGET
    "alternateDescription": string,
    "amountDue": number,
    "description": string,
    /** ISO Formatted Due Date ex: "2018-05-23T18:03:32.599Z" */
    "dueDate": string,
    "sequenceId": number,
}

export type PropertyAddressResponse = Address & {
    amountDue: number,
    lineItemDetails?: PropertyAddressLineItemDetail[],
}

@Injectable({ providedIn: "root" })
export class PropertyApi extends HOAApiBase{

    constructor() {
        super('PropertyApi');
    }

    /**
     * Retrieve the convenience fee for a property
     * In the mobile and web app, the user can modify the payment amount. Convenience fees may be percentage-based, so we need to recalculate the convenience fee\n
     * @param propertyId Unique identifier for a property
     * @param amount Amount user is intending to pay
     */
    // This does not appear to be used except in a unit test
    public convenienceFeePropertyIdAmountGet(propertyId: number, amount: number): Observable<ConvenienceFeeResponse> {
        if (!propertyId) {
            throw new Error('Missing required parameter propertyId when calling convenienceFeePropertyIdAmountGet');
        }
        if (!amount) {
            throw new Error('Missing required parameter amount when calling convenienceFeePropertyIdAmountGet');
        }

        return this.http.get(this.createUrl(this.getBasePath(), `/convenienceFee/${propertyId}/${amount}`))
    }

    /**
     * Retrieve HOA coupon data by the user&#39;s property address
     * Given an address, the service will return the HOA, PMC and Property identifiers\n
     * @param isAnonymous Boolean to determine if we are calling this outside of the registered user space
     * @param address Property address entered by user
     */
    public couponGet(isAnonymous: boolean, address: Address): Observable<CouponData> {
        if (!address) {
            throw new Error('Missing required parameter address when calling couponLookupByPropertyAddressGet');
        }

        const url = isAnonymous ?
            this.createUrl(this.getLocalPath() , '/v1/registration/coupon') :
            this.createUrl(this.getBasePath(), '/coupon');

        const queryParameters: any = {
            line1: address.line1,
            city: address.city,
            state: address.state,
            postalCode: address.postalCode,
            country: "USA"
        };

        let finalParams = queryParameters;

        if (address.line2) {
            finalParams = Object.assign({}, queryParameters, { line2: address.line2 })
        }
        if (address.unit) {
            finalParams  = Object.assign({}, queryParameters,{ unit: address.unit })
        }

        return this.http.get<CouponData>(url, { params: finalParams });
    }

    /**
     * Retrieve the user&#39;s property address based on coupond data.
     * Given a PMC, HOA and Property ID, the service will return the property address
     * @param isAnonymous Boolean to determine if we are calling this outside of the registered user space
     * @param managementId String identifier for Property Management Company (PMC)
     * @param associationId String identifier for the Homeowner&#39;s/Renter&#39;s association (HOA)
     * @param propertyId String identifier for the property
     */
    public propertyAddressGet(isAnonymous: boolean, managementId: String, associationId: String, propertyId: String): Observable<PropertyAddressResponse> {
        if (!managementId || !associationId || !propertyId) {
            throw new Error('Missing required parameter address when calling propertyAddressGet');
        }

        const url = isAnonymous ?
            this.createUrl(this.getLocalPath(),"/v1/registration/propertyAddress") :
            this.createUrl(this.getBasePath(), '/propertyAddress');

        let queryParameters: any = {
            pmcCouponId: managementId,
            hoaCouponId: associationId,
            propertyCouponId: propertyId
        };

        return this.http.get<PropertyAddressResponse>(url, { params: queryParameters })
    }

    /**
     * List Properties
     * Returns a list of Property objects, without the payment history data\n
     * @param id Unique identifier for the user.
     */
    public homeownersIdPropertiesGet(id: number): Observable<Property[]> {
        if (!id) {
            throw new Error('Missing required parameter id when calling homeownersIdPropertiesGet');
        }

        return this.http.get<Property[]>(this.createUrl(this.getBasePath() , `/homeowners/${id}/properties`));
    }

    /**
     * Save a new Property
     * Save new property information for a user.\n
     * @param id User id to associate with the property
     * @param property Property
     * @param isAnonymous Anonymous
     */
    public homeownersIdPropertiesPost(isAnonymous: boolean, id: number, property: Property): Observable<HttpResponse<Property>> {
        // verify required parameter 'id' is set
        if (!id) {
            throw new Error('Missing required parameter id when calling homeownersIdPropertiesPost');
        }
        // verify required parameter 'property' is set
        if (!property) {
            throw new Error('Missing required parameter property when calling homeownersIdPropertiesPost');
        }

        const localVarPath = isAnonymous ?
            this.createUrl(this.getLocalPath() , '/guestPayment/_saveProperty?homeownerId=' + id) :
            this.createUrl(this.getBasePath() ,`/homeowners/${id}/properties`);

        return this.http.post<Property>(localVarPath, this.formatProperty(property), {
            observe: "response"
        })
    }

    public formatProperty(property: Property) {
        return {
            address: {
                city: property.address.city,
                country: 'US',
                line1: property.address.line1,
                line2: property.address.line2,
                postalCode: property.address.postalCode,
                state: property.address.state
            },
            couponId: property.couponId,
            hoa: {
                couponId: property.hoa.couponId,
                pmc: {
                    couponId: property.hoa.pmc.couponId
                }
            },
            nickname: (property.nickname) ? property.nickname : property.address.line1
        };
    }

    public propertiesValidateGet(isAnonymous: boolean, managementId: string, associationId: string,
        propertyOrUnitNumber: string): Observable<ValidationResponse> {
        if (!associationId) {
            throw new Error('Missing required parameter associationId when calling propertiesValidate');
        }
        if (!propertyOrUnitNumber) {
            throw new Error('Missing required parameter propertyId when calling propertiesValidate');
        }

        const localVarPath = isAnonymous
            ? this.createUrl(this.getLocalPath(),'/v1/registration/_validate')
            : this.createUrl(this.getBasePath(),'/properties/_validate');

        //create parameter object
        const queryParameters: any = {
            hoaCouponId: associationId,
            propertyCouponId: propertyOrUnitNumber
        };

        let finalParams = queryParameters;

        if (managementId) {
            finalParams = Object.assign({}, queryParameters, { pmcCouponId: managementId });
        }

        return this.http.get<ValidationResponse>(localVarPath, { params: finalParams })
    }

    /**
     * Delete Property
     * Method to delete a property for a given user\n
     * @param id Unique id for the property
     */
    public propertiesIdDelete(id: number): Observable<HttpResponse<Confirmation>> {
        if (!id) {
            throw new Error('Missing required parameter id when calling propertiesIdDelete');
        }

        return this.http.delete<Confirmation>(this.createUrl(this.getBasePath(),`/properties/${id}`), {
            observe: 'response'
        });
    }

    /**
     * Load Property Detail
     * Load detail for a property, most recent 3 payments associated with the property, and if there are any payment schedules associated to it.\n
     * @param id Property ID
     */
    public propertiesIdGet(id: number): Observable<Property> {
        if (!id) {
            throw new Error('Missing required parameter id when calling propertiesIdGet');
        }

        return this.http.get(this.createUrl(this.getBasePath(),`/properties/${id}`))
    }

    /**
     * Payment History
     * Retrieve the payment history for a given property\n
     * @param id DB Id of the property
     */
    public propertiesIdPaymentHistoriesGet(id: number): Observable<PaymentHistory[]> {
        if (!id) {
            throw new Error('Missing required parameter id when calling propertiesIdPaymentHistoriesGet');
        }

        return this.http.get<PaymentHistory[]>(this.createUrl(this.getBasePath(),`/properties/${id}/paymentHistories`))
    }
    /**
     * List the payment schedules associated with a property.
     * Retrieve the list of payment schedules for a property.\n
     * @param id Unique identifier for a property
     */
    public propertiesIdPaymentSchedulesGet(id: number): Observable<PaymentSchedule[]> {
        if (!id) {
            throw new Error('Missing required parameter id when calling propertiesIdPaymentSchedulesGet');
        }

        return this.http.get<PaymentSchedule[]>(this.createUrl(this.getBasePath(),`/properties/${id}/paymentSchedules`))
    }
    /**
     * Update Property Detail
     * Update existing property information.\n
     * @param id ID of the property being updated
     * @param property Property
     */
    public propertiesIdPost(id: number, property: Property): Observable<string> {
        if (!id) {
            throw new Error('Missing required parameter id when calling propertiesIdPost');
        }
        if (!property) {
            throw new Error('Missing required parameter property when calling propertiesIdPost');
        }

        return this.http.post<string>(this.createUrl(this.getBasePath(),`/properties/${id}`), property);
    }

    /**
     * Return active content defined as, return both PMC and HOA if they exist, return PMC if it exists, return HOA if it exists, otherwise return nothing
     * @param isAnonymous Boolean to determine if we are calling this outside of the registered user space
     * @param pmcCouponId String identifier for Property Management Company (PMC)
     * @param locationType String identifier for the message location
     * @param hoaCouponId String identifier for the Homeowners/Renters association (HOA)
     */
    public propertiesActiveContentGet(isAnonymous: boolean, pmcCouponId: String, locationType: String, hoaCouponId?: String): Observable<Content[]> {
        // check isAnonymous here if/when we add messages to guest
        let queryParameters: any = {
            hoaCouponId: hoaCouponId,
        };

        return this.http.get<Content[]>(this.createUrl(this.getBasePath(),`/content/coupon/${pmcCouponId}/type/${locationType}/active`), {
            params: queryParameters
        })
    }
}

